import React, { useState, useEffect, useCallback } from 'react';
import { Send, Plus, Users, BarChart3, Mail, X, Copy, Eye, Play, Ban, Search, ChevronLeft, MousePointerClick, AlertTriangle, CheckCircle, Pencil } from 'lucide-react';
import { Panel, Section, Button, Badge, LoadingSpinner } from './components';
import EmailBlockEditor, { normalizeBlocks } from './components/EmailBlockEditor';
import SegmentFilters from './components/SegmentFilters';
import apiService from './services/api';

function CampaignFormModal({ campaign, tags, onClose, onSave }) {
  const isEditing = !!campaign;
  const [form, setForm] = useState({
    name: '', subject: '', from_name: 'Amour Mobile Nails',
    from_email: 'app@amourmobilenails.com', reply_to: 'info@amourmobilenails.com',
    preview_text: '', html_content: '', text_content: '',
    blocks: [], send_to_all: false, tag_ids: [], segment_filters: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [audienceCount, setAudienceCount] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [contentMode, setContentMode] = useState('blocks'); // 'blocks' | 'raw' | 'edit'
  const editFrameRef = React.useRef(null);

  useEffect(() => {
    apiService.getEmailTemplates().then(data => {
      setTemplates(Array.isArray(data) ? data : data.results || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (campaign) {
      const rawBlocks = campaign.blocks || [];
      const flatBlocks = normalizeBlocks(rawBlocks);
      const hasBlocks = flatBlocks.length > 0;
      setForm({
        name: campaign.name || '', subject: campaign.subject || '',
        from_name: campaign.from_name || 'Amour Mobile Nails',
        from_email: campaign.from_email || 'app@amourmobilenails.com',
        reply_to: campaign.reply_to || 'info@amourmobilenails.com',
        preview_text: campaign.preview_text || '',
        html_content: campaign.html_content || '', text_content: campaign.text_content || '',
        blocks: flatBlocks,
        send_to_all: campaign.send_to_all || false,
        tag_ids: campaign.tags ? campaign.tags.map(t => t.id) : [],
        segment_filters: campaign.segment_filters || null,
      });
      setContentMode(hasBlocks ? 'blocks' : (campaign.html_content ? 'edit' : 'blocks'));
    } else {
      // New campaign: load default template blocks
      apiService.getEmailTemplates().then(data => {
        const list = Array.isArray(data) ? data : data.results || [];
        const defaultTpl = list.find(t => t.is_default);
        if (defaultTpl && defaultTpl.blocks) {
          setForm(prev => ({ ...prev, blocks: defaultTpl.blocks }));
        }
      }).catch(() => {});
    }
  }, [campaign]);

  const captureEditedHtml = () => {
    const iframe = editFrameRef.current;
    if (!iframe || !iframe.contentDocument) return null;
    const body = iframe.contentDocument.body;
    body.querySelectorAll('[contenteditable]').forEach(el => el.removeAttribute('contenteditable'));
    return '<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head><body style="margin:0;padding:0;background-color:#F2F2F2;font-family:Helvetica,Arial,sans-serif;-webkit-text-size-adjust:100%">' + body.innerHTML + '</body></html>';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.subject) {
      setError('Name and subject are required');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload = { ...form };
      // Auto-capture edits from the visual editor before saving
      if (contentMode === 'edit') {
        const captured = captureEditedHtml();
        if (captured) payload.html_content = captured;
      }
      // If using blocks mode, clear raw html so the backend renders from blocks
      if (contentMode === 'blocks' && payload.blocks && payload.blocks.length > 0) {
        payload.html_content = '';
      }
      // If using raw or edit mode, clear blocks so backend uses html_content
      if (contentMode === 'raw' || contentMode === 'edit') {
        payload.blocks = null;
      }
      if (isEditing) {
        await apiService.updateCampaign(campaign.id, payload);
      } else {
        await apiService.createCampaign(payload);
      }
      onSave();
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  const resolveAudience = async () => {
    if (!isEditing) return;
    try {
      const data = await apiService.resolveCampaignAudience(campaign.id);
      setAudienceCount(data.count);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (isEditing) resolveAudience();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleTag = (tagId) => {
    setForm(prev => ({
      ...prev,
      tag_ids: prev.tag_ids.includes(tagId)
        ? prev.tag_ids.filter(id => id !== tagId)
        : [...prev.tag_ids, tagId],
    }));
  };

  const handleLoadTemplate = (template) => {
    if (template.blocks) {
      setForm(prev => ({ ...prev, blocks: [...template.blocks] }));
      setContentMode('blocks');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-sm font-semibold">{isEditing ? 'Edit Campaign' : 'New Campaign'}</h2>
          <button onClick={onClose}><X className="w-4 h-4 text-gray-400" /></button>
        </div>
        {error && (
          <div className="mx-4 mt-3 p-2 bg-red-50 border border-red-200 rounded">
            <p className="text-xs text-red-800">{error}</p>
          </div>
        )}
        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Campaign Name</label>
            <input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})}
              className="w-full px-3 py-1.5 text-sm border rounded focus:ring-2 focus:ring-red-800 focus:border-red-800 outline-none"
              placeholder="e.g. Valentine's Day Promo" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Subject Line</label>
            <input type="text" value={form.subject} onChange={e => setForm({...form, subject: e.target.value})}
              className="w-full px-3 py-1.5 text-sm border rounded focus:ring-2 focus:ring-red-800 focus:border-red-800 outline-none"
              placeholder="e.g. Special offer just for you!" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">From Name</label>
              <input type="text" value={form.from_name} onChange={e => setForm({...form, from_name: e.target.value})}
                className="w-full px-3 py-1.5 text-sm border rounded focus:ring-2 focus:ring-red-800 focus:border-red-800 outline-none" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Preview Text</label>
              <input type="text" value={form.preview_text} onChange={e => setForm({...form, preview_text: e.target.value})}
                className="w-full px-3 py-1.5 text-sm border rounded focus:ring-2 focus:ring-red-800 focus:border-red-800 outline-none"
                placeholder="Shows in inbox preview" />
            </div>
          </div>

          {/* Targeting */}
          <div className="border-t pt-3">
            <label className="block text-xs font-medium text-gray-700 mb-2">Targeting</label>
            <label className="flex items-center gap-2 mb-2">
              <input type="checkbox" checked={form.send_to_all}
                onChange={e => setForm({...form, send_to_all: e.target.checked})}
                className="rounded text-red-800 focus:ring-red-800" />
              <span className="text-xs text-gray-700">Send to all active subscribers</span>
            </label>
            {!form.send_to_all && (
              <>
                {tags.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Select tags:</p>
                    <div className="flex flex-wrap gap-1">
                      {tags.map(tag => (
                        <button key={tag.id} type="button" onClick={() => toggleTag(tag.id)}
                          className={`px-2 py-0.5 rounded-full text-xs border transition-colors ${
                            form.tag_ids.includes(tag.id)
                              ? 'bg-red-800 text-white border-red-800'
                              : 'bg-white text-gray-600 border-gray-300 hover:border-red-800'
                          }`}>
                          {tag.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <SegmentFilters
                  filters={form.segment_filters}
                  onChange={sf => setForm(prev => ({ ...prev, segment_filters: sf }))}
                />
              </>
            )}
            {isEditing && audienceCount !== null && (
              <p className="text-xs text-gray-500 mt-2">
                Estimated audience: <span className="font-semibold text-gray-900">{audienceCount}</span> subscribers
              </p>
            )}
          </div>

          {/* Content mode toggle */}
          <div className="border-t pt-3">
            <div className="flex items-center gap-2 mb-2">
              <label className="text-xs font-medium text-gray-700">Content Mode:</label>
              <button type="button" onClick={() => setContentMode('blocks')}
                className={`px-2 py-0.5 text-xs border rounded ${contentMode === 'blocks' ? 'bg-red-800 text-white border-red-800' : 'text-gray-600 hover:bg-gray-50'}`}>
                Visual Editor
              </button>
              {form.html_content && (
                <button type="button" onClick={() => setContentMode('edit')}
                  className={`px-2 py-0.5 text-xs border rounded ${contentMode === 'edit' ? 'bg-red-800 text-white border-red-800' : 'text-gray-600 hover:bg-gray-50'}`}>
                  Edit Text
                </button>
              )}
              <button type="button" onClick={() => setContentMode('raw')}
                className={`px-2 py-0.5 text-xs border rounded ${contentMode === 'raw' ? 'bg-red-800 text-white border-red-800' : 'text-gray-600 hover:bg-gray-50'}`}>
                Raw HTML
              </button>
            </div>
          </div>

          {/* Block editor */}
          {contentMode === 'blocks' && (
            <EmailBlockEditor
              blocks={form.blocks || []}
              onChange={blocks => setForm(prev => ({ ...prev, blocks }))}
              templates={templates}
              onLoadTemplate={handleLoadTemplate}
            />
          )}

          {/* Visual text editor — renders HTML, lets you click and edit text */}
          {contentMode === 'edit' && form.html_content && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Click on any text below to edit it directly</label>
              <div className="border rounded overflow-hidden bg-white" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                <iframe
                  ref={editFrameRef}
                  title="Email editor"
                  style={{ width: '100%', height: '500px', border: 'none' }}
                  srcDoc={`<!DOCTYPE html><html><head><style>body{margin:0;} [contenteditable]:hover{outline:2px dashed #B22222;outline-offset:2px;cursor:text;} [contenteditable]:focus{outline:2px solid #B22222;outline-offset:2px;background:rgba(178,34,34,0.03);}</style></head><body>${form.html_content.replace(/<body[^>]*>/, '').replace(/<\/body>/, '').replace(/<html[^>]*>/, '').replace(/<\/html>/, '').replace(/<!DOCTYPE[^>]*>/, '').replace(/<head>[\s\S]*?<\/head>/, '')}</body></html>`}
                  onLoad={() => {
                    const iframe = editFrameRef.current;
                    if (!iframe) return;
                    const doc = iframe.contentDocument;
                    if (!doc) return;
                    // Make all text nodes editable
                    const els = doc.querySelectorAll('td, p, span, a, h1, h2, h3, h4, em, strong');
                    els.forEach(el => {
                      if (el.children.length === 0 || (el.textContent && el.textContent.trim().length > 0 && el.querySelectorAll('td, p, span').length === 0)) {
                        el.setAttribute('contenteditable', 'true');
                      }
                    });
                    // Prevent link navigation
                    doc.addEventListener('click', e => {
                      if (e.target.tagName === 'A' || e.target.closest('a')) {
                        e.preventDefault();
                      }
                    });
                  }}
                />
              </div>
              <button type="button" onClick={() => {
                const iframe = editFrameRef.current;
                if (!iframe || !iframe.contentDocument) return;
                const body = iframe.contentDocument.body;
                // Remove contenteditable attributes before capturing
                body.querySelectorAll('[contenteditable]').forEach(el => el.removeAttribute('contenteditable'));
                const edited = '<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head><body style="margin:0;padding:0;background-color:#F2F2F2;font-family:Helvetica,Arial,sans-serif;-webkit-text-size-adjust:100%">' + body.innerHTML + '</body></html>';
                setForm(prev => ({ ...prev, html_content: edited }));
                alert('Changes captured! Click "Update Campaign" to save.');
              }} className="mt-2 px-3 py-1.5 text-xs bg-red-800 text-white rounded hover:bg-red-900">
                Capture Text Changes
              </button>
            </div>
          )}

          {/* Raw HTML editor (fallback) */}
          {contentMode === 'raw' && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">HTML Content</label>
                <textarea value={form.html_content} onChange={e => setForm({...form, html_content: e.target.value})}
                  className="w-full px-3 py-1.5 text-sm border rounded focus:ring-2 focus:ring-red-800 focus:border-red-800 outline-none font-mono"
                  rows={10} placeholder="Paste your HTML email content here..." />
              </div>
            </>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Plain Text Fallback</label>
            <textarea value={form.text_content} onChange={e => setForm({...form, text_content: e.target.value})}
              className="w-full px-3 py-1.5 text-sm border rounded focus:ring-2 focus:ring-red-800 focus:border-red-800 outline-none"
              rows={3} placeholder="Plain text version for email clients that don't support HTML" />
          </div>

          <div className="flex gap-2 pt-2 border-t">
            <Button type="submit" variant="primary" size="sm" disabled={submitting}>
              {submitting ? 'Saving...' : (isEditing ? 'Update Campaign' : 'Create Campaign')}
            </Button>
            <Button type="button" variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CampaignDetailView({ campaign, onBack, onRefresh, onEdit }) {
  const [recipients, setRecipients] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [previewEmail, setPreviewEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [audienceCount, setAudienceCount] = useState(campaign.estimated_audience ?? null);

  useEffect(() => {
    if (campaign.status !== 'draft') {
      loadRecipients();
    } else if (audienceCount === null) {
      apiService.resolveCampaignAudience(campaign.id)
        .then(data => setAudienceCount(data.count))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign.id]);

  const loadRecipients = async () => {
    setLoadingRecipients(true);
    try {
      const data = await apiService.getCampaignRecipients(campaign.id);
      setRecipients(Array.isArray(data) ? data : data.results || []);
    } catch { /* ignore */ }
    setLoadingRecipients(false);
  };

  const handleSend = async () => {
    if (!window.confirm(`Send "${campaign.name}" to all targeted subscribers? This cannot be undone.`)) return;
    setSending(true);
    setActionError(null);
    try {
      await apiService.sendCampaign(campaign.id);
      onRefresh();
    } catch (err) {
      setActionError(err.message);
    }
    setSending(false);
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel this campaign?')) return;
    try {
      await apiService.cancelCampaign(campaign.id);
      onRefresh();
    } catch (err) {
      setActionError(err.message);
    }
  };

  const handleDuplicate = async () => {
    try {
      await apiService.duplicateCampaign(campaign.id);
      onRefresh();
      onBack();
    } catch (err) {
      setActionError(err.message);
    }
  };

  const handlePreview = async () => {
    if (!previewEmail) return;
    try {
      await apiService.previewCampaign(campaign.id, previewEmail);
      alert(`Preview sent to ${previewEmail}`);
      setPreviewEmail('');
    } catch (err) {
      setActionError(err.message);
    }
  };

  const statusVariant = {
    draft: 'neutral', scheduled: 'info', sending: 'warning', sent: 'success', cancelled: 'error',
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <button onClick={onBack} className="text-gray-400 hover:text-gray-600">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="flex-1">
          <h2 className="text-sm font-semibold text-gray-900">{campaign.name}</h2>
          <p className="text-xs text-gray-500">{campaign.subject}</p>
        </div>
        <Badge variant={statusVariant[campaign.status] || 'neutral'} small>{campaign.status}</Badge>
      </div>

      {actionError && (
        <div className="p-2 bg-red-50 border border-red-200 rounded">
          <p className="text-xs text-red-800">{actionError}</p>
        </div>
      )}

      {/* Stats */}
      {campaign.status === 'draft' ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Panel><div className="p-2 text-center"><p className="text-xs text-gray-500">Estimated Audience</p><p className="text-lg font-bold">{audienceCount != null ? `~${audienceCount.toLocaleString()}` : '--'}</p></div></Panel>
          <Panel><div className="p-2 text-center"><p className="text-xs text-gray-500">Status</p><p className="text-lg font-bold capitalize">{campaign.status}</p></div></Panel>
          <Panel><div className="p-2 text-center"><p className="text-xs text-gray-500">Blocks</p><p className="text-lg font-bold">{campaign.blocks?.length || 0}</p></div></Panel>
          <Panel><div className="p-2 text-center"><p className="text-xs text-gray-500">Created</p><p className="text-sm font-bold">{new Date(campaign.created_at).toLocaleDateString()}</p></div></Panel>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Panel><div className="p-2 text-center"><p className="text-xs text-gray-500">Sent</p><p className="text-lg font-bold">{campaign.sent_count || 0}</p></div></Panel>
          <Panel><div className="p-2 text-center"><p className="text-xs text-gray-500">Delivered</p><p className="text-lg font-bold">{campaign.delivered_count || 0}</p></div></Panel>
          <Panel><div className="p-2 text-center"><p className="text-xs text-gray-500">Open Rate</p><p className="text-lg font-bold">{campaign.open_rate || 0}%</p></div></Panel>
          <Panel><div className="p-2 text-center"><p className="text-xs text-gray-500">Click Rate</p><p className="text-lg font-bold">{campaign.click_rate || 0}%</p></div></Panel>
        </div>
      )}

      {/* Detailed stats */}
      {campaign.status === 'sent' && (
        <Panel>
          <div className="p-2 grid grid-cols-4 gap-2 text-center text-xs">
            <div><span className="text-gray-500">Opened</span><p className="font-semibold">{campaign.opened_count || 0}</p></div>
            <div><span className="text-gray-500">Clicked</span><p className="font-semibold">{campaign.clicked_count || 0}</p></div>
            <div><span className="text-gray-500">Bounced</span><p className="font-semibold text-amber-600">{campaign.bounced_count || 0}</p></div>
            <div><span className="text-gray-500">Unsubscribed</span><p className="font-semibold text-red-600">{campaign.unsubscribed_count || 0}</p></div>
          </div>
        </Panel>
      )}

      {/* Actions */}
      <Panel>
        <div className="p-2 space-y-2">
          <h3 className="text-xs font-medium text-gray-700">Actions</h3>
          <div className="flex flex-wrap gap-2">
            {(campaign.status === 'draft' || campaign.status === 'scheduled') && onEdit && (
              <Button variant="secondary" size="sm" onClick={() => onEdit(campaign)}>
                <Pencil className="w-3 h-3 mr-1" />Edit Content
              </Button>
            )}
            {campaign.status === 'draft' && (
              <Button variant="primary" size="sm" onClick={handleSend} disabled={sending}>
                <Play className="w-3 h-3 mr-1" />{sending ? 'Queuing...' : 'Send Now'}
              </Button>
            )}
            {(campaign.status === 'scheduled' || campaign.status === 'sending') && (
              <Button variant="secondary" size="sm" onClick={handleCancel}>
                <Ban className="w-3 h-3 mr-1" />Cancel
              </Button>
            )}
            <Button variant="secondary" size="sm" onClick={handleDuplicate}>
              <Copy className="w-3 h-3 mr-1" />Duplicate
            </Button>
          </div>
          {/* Preview */}
          <div className="flex gap-2 items-center mt-2">
            <input type="email" value={previewEmail} onChange={e => setPreviewEmail(e.target.value)}
              placeholder="Email for test preview" className="flex-1 px-2 py-1 text-xs border rounded outline-none focus:ring-1 focus:ring-red-800" />
            <Button variant="secondary" size="sm" onClick={handlePreview} disabled={!previewEmail}>
              <Eye className="w-3 h-3 mr-1" />Preview
            </Button>
          </div>
        </div>
      </Panel>

      {/* Recipients */}
      {campaign.status !== 'draft' && (
        <Panel>
          <Section title={`Recipients (${campaign.recipient_count || 0})`}>
            {loadingRecipients ? <LoadingSpinner size="sm" message="Loading recipients..." /> : (
              recipients.length === 0 ? (
                <p className="text-xs text-gray-400 py-4 text-center">No recipients yet</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-2 py-1 text-left text-xs font-medium text-gray-500">Email</th>
                        <th className="px-2 py-1 text-left text-xs font-medium text-gray-500">Status</th>
                        <th className="px-2 py-1 text-left text-xs font-medium text-gray-500">Sent</th>
                        <th className="px-2 py-1 text-left text-xs font-medium text-gray-500">Opened</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {recipients.slice(0, 100).map(r => (
                        <tr key={r.id} className="hover:bg-gray-50">
                          <td className="px-2 py-1 text-xs">{r.email}</td>
                          <td className="px-2 py-1">
                            <Badge small variant={
                              r.status === 'delivered' ? 'success' :
                              r.status === 'opened' || r.status === 'clicked' ? 'info' :
                              r.status === 'bounced' || r.status === 'failed' ? 'error' :
                              r.status === 'sent' ? 'warning' : 'neutral'
                            }>{r.status}</Badge>
                          </td>
                          <td className="px-2 py-1 text-xs text-gray-500">{r.sent_at ? new Date(r.sent_at).toLocaleString() : '--'}</td>
                          <td className="px-2 py-1 text-xs text-gray-500">{r.opened_at ? new Date(r.opened_at).toLocaleString() : '--'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {recipients.length > 100 && (
                    <p className="text-xs text-gray-400 text-center py-2">Showing first 100 of {recipients.length}</p>
                  )}
                </div>
              )
            )}
          </Section>
        </Panel>
      )}
    </div>
  );
}

function CampaignsPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState(null);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (searchTerm) params.search = searchTerm;

      const [campaignData, statsData, tagData] = await Promise.all([
        apiService.getCampaigns(params),
        apiService.getCampaignStats(),
        apiService.getTags(),
      ]);
      setCampaigns(Array.isArray(campaignData) ? campaignData : campaignData.results || []);
      setStats(statsData);
      setTags(Array.isArray(tagData) ? tagData : tagData.results || []);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, [statusFilter, searchTerm]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleRowClick = async (campaign) => {
    try {
      const full = await apiService.getCampaign(campaign.id);
      setSelectedCampaign(full);
    } catch {
      setSelectedCampaign(campaign);
    }
  };

  const handleEdit = async (campaign, e) => {
    e.stopPropagation();
    try {
      const full = await apiService.getCampaign(campaign.id);
      setEditingCampaign(full);
    } catch {
      setEditingCampaign(campaign);
    }
    setShowModal(true);
  };

  const handleDelete = async (campaign, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete campaign "${campaign.name}"?`)) return;
    try {
      await apiService.deleteCampaign(campaign.id);
      loadData();
    } catch (err) {
      alert(err.message);
    }
  };

  const statusVariant = {
    draft: 'neutral', scheduled: 'info', sending: 'warning', sent: 'success', cancelled: 'error',
  };

  if (selectedCampaign) {
    return (
      <div className="flex-1 bg-neutral-50 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
        <div className="p-3">
          <CampaignDetailView
            campaign={selectedCampaign}
            onBack={() => { setSelectedCampaign(null); loadData(); }}
            onRefresh={async () => {
              const updated = await apiService.getCampaign(selectedCampaign.id);
              setSelectedCampaign(updated);
            }}
            onEdit={(c) => { setEditingCampaign(c); setShowModal(true); }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-neutral-50 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
      <div className="p-3">
        {/* Header */}
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-gray-900 leading-none">Email Campaigns</h1>
            <p className="text-xs text-gray-500 mt-0.5">Send targeted emails to your clients via Mailgun</p>
          </div>
          <Button variant="primary" size="sm" onClick={() => { setEditingCampaign(null); setShowModal(true); }}>
            <Plus className="w-3.5 h-3.5 mr-1" />New Campaign
          </Button>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
          <Panel>
            <div className="flex items-center gap-2 p-1">
              <div className="p-1.5 bg-red-50 rounded"><Send className="w-4 h-4 text-red-800" /></div>
              <div><p className="text-xs text-gray-500">Campaigns</p><p className="text-sm font-semibold text-gray-900">{stats?.total_campaigns || 0}</p></div>
            </div>
          </Panel>
          <Panel>
            <div className="flex items-center gap-2 p-1">
              <div className="p-1.5 bg-blue-50 rounded"><Mail className="w-4 h-4 text-blue-600" /></div>
              <div><p className="text-xs text-gray-500">Emails Sent</p><p className="text-sm font-semibold text-gray-900">{stats?.total_sent || 0}</p></div>
            </div>
          </Panel>
          <Panel>
            <div className="flex items-center gap-2 p-1">
              <div className="p-1.5 bg-green-50 rounded"><BarChart3 className="w-4 h-4 text-green-600" /></div>
              <div><p className="text-xs text-gray-500">Avg Open Rate</p><p className="text-sm font-semibold text-gray-900">{stats?.avg_open_rate || '--'}%</p></div>
            </div>
          </Panel>
          <Panel>
            <div className="flex items-center gap-2 p-1">
              <div className="p-1.5 bg-purple-50 rounded"><Users className="w-4 h-4 text-purple-600" /></div>
              <div><p className="text-xs text-gray-500">Subscribers</p><p className="text-sm font-semibold text-gray-900">{stats?.active_subscribers || '--'}</p></div>
            </div>
          </Panel>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-3">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search campaigns..." className="w-full pl-8 pr-3 py-1.5 text-xs border rounded outline-none focus:ring-1 focus:ring-red-800" />
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="px-2 py-1.5 text-xs border rounded outline-none focus:ring-1 focus:ring-red-800">
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="scheduled">Scheduled</option>
            <option value="sending">Sending</option>
            <option value="sent">Sent</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        {/* Campaign List */}
        <Panel>
          <Section title="All Campaigns">
            {loading ? <LoadingSpinner size="sm" message="Loading campaigns..." /> : error ? (
              <div className="text-center py-8">
                <AlertTriangle className="w-6 h-6 text-red-400 mx-auto mb-2" />
                <p className="text-xs text-red-600">{error}</p>
                <Button variant="secondary" size="sm" onClick={loadData} className="mt-2">Retry</Button>
              </div>
            ) : campaigns.length === 0 ? (
              <div className="text-center py-12">
                <Send className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500 mb-1">No campaigns yet</p>
                <p className="text-xs text-gray-400 mb-3">Create your first email campaign to reach your clients</p>
                <Button variant="primary" size="sm" onClick={() => { setEditingCampaign(null); setShowModal(true); }}>
                  <Plus className="w-3.5 h-3.5 mr-1" />Create Campaign
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Campaign</th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Status</th>
                      <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Recipients</th>
                      <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Open Rate</th>
                      <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Click Rate</th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Sent</th>
                      <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {campaigns.map((c) => (
                      <tr key={c.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleRowClick(c)}>
                        <td className="px-2 py-1.5">
                          <p className="text-xs font-medium text-gray-900">{c.name}</p>
                          <p className="text-xs text-gray-500">{c.subject}</p>
                        </td>
                        <td className="px-2 py-1.5">
                          <Badge variant={statusVariant[c.status] || 'neutral'} small>{c.status}</Badge>
                        </td>
                        <td className="px-2 py-1.5 text-xs text-gray-900 text-right">
                          {c.status === 'draft' || c.status === 'scheduled'
                            ? (c.estimated_audience != null ? `~${c.estimated_audience.toLocaleString()}` : '--')
                            : (c.recipient_count || 0).toLocaleString()}
                        </td>
                        <td className="px-2 py-1.5 text-xs text-gray-900 text-right">{c.open_rate != null ? `${c.open_rate}%` : '--'}</td>
                        <td className="px-2 py-1.5 text-xs text-gray-900 text-right">{c.click_rate != null ? `${c.click_rate}%` : '--'}</td>
                        <td className="px-2 py-1.5 text-xs text-gray-500">{c.sent_at ? new Date(c.sent_at).toLocaleDateString() : '--'}</td>
                        <td className="px-2 py-1.5 text-right space-x-1">
                          {c.status === 'draft' && (
                            <button onClick={(e) => handleEdit(c, e)} className="text-xs text-blue-600 hover:underline">Edit</button>
                          )}
                          {c.status === 'draft' && (
                            <button onClick={(e) => handleDelete(c, e)} className="text-xs text-red-600 hover:underline">Del</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        </Panel>
      </div>

      {showModal && (
        <CampaignFormModal
          campaign={editingCampaign}
          tags={tags}
          onClose={() => { setShowModal(false); setEditingCampaign(null); }}
          onSave={async () => {
            setShowModal(false);
            const editedId = editingCampaign?.id;
            setEditingCampaign(null);
            loadData();
            if (selectedCampaign && editedId === selectedCampaign.id) {
              try {
                const updated = await apiService.getCampaign(editedId);
                setSelectedCampaign(updated);
              } catch { /* ignore */ }
            }
          }}
        />
      )}
    </div>
  );
}

export default CampaignsPage;
